import React from "react";

import { AdonisIdentity } from "../../adonis.types";
import { useToolStatus } from "../../AdonisChatComponent/AdonistToolCalResponseProvider";
import { AssistantMessage as AssistantMessageModel } from "../../streaming/streaming-message-types";
import { AssistantHeader } from "../content/AssistantHeader";
import { MarkdownContent } from "../content/MarkdownContent";
import { MessageBubble } from "../primitives/MessageBubble";
import { MessageShell } from "../primitives/MessageShell";
import { ToolStatusLine } from "../ToolStatusLine";
// import { useToolStatus } from "../../AdonisChatComponent/AdonisChatComponent";

export interface AssistantMessageProps {
  message: AssistantMessageModel;
  disableAnimations?: boolean;
  persona: AdonisIdentity;
  toolStatusRenderer?: (params: {
    toolName: string;
    toolId: string;
    status: "pending" | "success" | "failed";
    persona: AdonisIdentity;
    input?: Record<string, unknown> | undefined;
  }) => React.ReactNode;
}

const getContentFromState = (message: AssistantMessageModel) => {
  if (message.displayState.kind === "hidden") {
    return "";
  }
  if (message.displayState.kind === "displaying") {
    return message.displayState.visibleText ?? "";
  }
  const contentSoFar = message.content
    .map((c) => (c.type === "text" ? c.text : ""))
    .join("");

  return contentSoFar;
};

/**
 * AssistantMessage component for rendering AI assistant messages.
 * Feature-rich component with markdown rendering, avatars, typing indicators.
 */
export const AssistantMessage = ({
  message,
  disableAnimations = false,
  persona,
  toolStatusRenderer,
}: AssistantMessageProps) => {
  const content = getContentFromState(message);
  const isStreamingEmpty =
    message.displayState?.kind === "displaying" &&
    (message.displayState.visibleText?.length ?? 0) === 0;

  // TODO move somewhere else, split compponent into props and smart
  const toolContext = useToolStatus();

  // For initial streaming delay, do not render a message; the global indicator shows instead
  if (isStreamingEmpty) {
    return null;
  }

  const arrayOfStuff = Array.isArray(message.content)
    ? message.content
    : [{ type: "text" as const, text: message.content }];

  return (
    <MessageShell alignment="left" disableAnimations={disableAnimations}>
      <MessageBubble variant="assistant">
        <AssistantHeader persona={persona} isTyping={false} />
        {arrayOfStuff.map((contentBlock, index) => {
          const prevBlock = index > 0 ? arrayOfStuff[index - 1] : undefined;
          const tightenAbove =
            contentBlock.type === "tool_use" && prevBlock?.type === "text";
          switch (contentBlock.type) {
            case "text":
              return (
                <MarkdownContent
                  key={
                    // Prefer Anthropic message id for stability where available
                    message.messageId ?? `text-${index}`
                  }
                  content={content}
                />
              );
            case "tool_use": {
              // Only render tool calls after text animation completes
              if (message.displayState?.kind !== "displayed") {
                return null;
              }

              const toolStatus = toolContext.toolStatuses[contentBlock.id];
              const status =
                toolStatus?.status === "failed"
                  ? "failed"
                  : toolStatus?.status === "success"
                  ? "success"
                  : "pending";

              const rendered = toolStatusRenderer ? (
                toolStatusRenderer({
                  toolName: contentBlock.name,
                  toolId: contentBlock.id,
                  status,
                  persona,
                  input: (
                    contentBlock as unknown as {
                      input?: Record<string, unknown>;
                    }
                  ).input,
                })
              ) : (
                <ToolStatusLine
                  toolName={contentBlock.name}
                  toolId={contentBlock.id}
                  status={status}
                  enableAnimation={!disableAnimations}
                />
              );

              const isFullBleed =
                React.isValidElement(rendered) &&
                (rendered as React.ReactElement).props?.[
                  "data-adonis-full-bleed"
                ];

              if (isFullBleed) {
                return (
                  <div key={contentBlock.id} className="-mx-4">
                    {rendered}
                  </div>
                );
              }

              return (
                <div
                  key={contentBlock.id}
                  className={tightenAbove ? "-mt-2" : undefined}
                >
                  {rendered}
                </div>
              );
            }
          }
        })}
        {/* Show subtle indicator when tools are pending but text is still animating */}
      </MessageBubble>
    </MessageShell>
  );
};
